import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useState, useEffect } from 'react';
import { platformAlert } from '../utils/platformAlert';
import { useDeliveryAddressContext } from '../contexts/DeliveryAddressContext';
import GeocodingService from '../services/GeocodingService';
import { trackEvent } from '../services/posthog';
import { logger } from '../utils/logger';

export interface CartItem {
  id: string;
  product_id: string;
  user_id: string;
  quantity: number;
  pendingConfirmation?: boolean;
  confirmed?: boolean; // New field: item confirmed by seller
  requestId?: string;
  product: {
    id: string;
    title: string;
    price: number;
    images: string[];
    size?: string;
    quantity: number;  // Available quantity in stock
    vendor_id: string;
    vendor: {
      id: string;
      store_name: string;
      street_line1: string;
      city: string;
      state: string;
      zip_code: string;
      phone_number?: string;
      latitude?: number;
      longitude?: number;
    };
  };
}

interface ProductWithVendor {
  id: string;
  vendor_id: string;
  vendor: {
    id: string;
    store_name: string;
    latitude?: number;
    longitude?: number;
    delivery_radius?: string;
    phone_number?: string;
  };
}

interface CartItemWithVendor {
  product: {
    vendor_id: string;
    vendor: {
      id: string;
      store_name: string;
      phone_number?: string;
    };
  };
}

// Function to calculate distance between two points (same as in useProducts)
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in miles
}

export function useCart() {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<any>(null);
  const { selectedAddress } = useDeliveryAddressContext();

  // Get initial session
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user?.id) return;

    const channel = supabase
      .channel(`cart-product-availability-${session.user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'products',
        },
        (payload) => {
          const newRow: any = payload.new;
          const oldRow: any = payload.old;
          const quantityChanged = Number(newRow?.quantity ?? NaN) !== Number(oldRow?.quantity ?? NaN);
          const statusChanged = newRow?.status !== oldRow?.status;

          if (!quantityChanged && !statusChanged) return;

          // Always refresh cart when product quantity or status changes
          // This ensures cart displays updated quantities even if product is still available
          logger.debug('Cart', 'Product quantity/status changed, refreshing cart', {
            productId: newRow?.id,
            oldQuantity: oldRow?.quantity,
            newQuantity: newRow?.quantity,
            oldStatus: oldRow?.status,
            newStatus: newRow?.status,
          });
          
          queryClient.invalidateQueries({ queryKey: ['cart', session.user.id] }).catch((err) => {
            logger.warn('Cart', 'Failed to invalidate cart on product change', err);
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session?.user?.id, queryClient]);

  const { data: cartItems, isLoading, error, refetch } = useQuery({
    queryKey: ['cart', session?.user?.id],
    queryFn: async () => {
      // Check if user is logged in
      if (!session?.user) {
        logger.debug('Cart', 'No authenticated user found, returning empty cart');
        return [];
      }

      logger.debug('Cart', 'Fetching cart for user', { userId: session.user.id });

      try {
        const { data, error } = await supabase
          .from('cart_items')
          .select(`
            *,
            product:products(
              id,
              title,
              price,
              images,
              size,
              quantity,
              vendor_id,
              status,
              vendor:vendors(
                id,
                store_name,
                street_line1,
                city,
                state,
                zip_code,
                stripe_accountid,
                logo_image,
                phone_number,
                latitude,
                longitude
              )
            )
          `)
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false }); // Most recent first

        if (error) {
          logger.error('Cart', 'Error fetching cart', error);
          throw error;
        }

        if (!data || data.length === 0) {
          logger.debug('Cart', 'Cart is empty for user', { userId: session.user.id });
          return [];
        }

        logger.debug('Cart', 'Successfully fetched cart', { itemCount: data.length });

        // Process items: filter invalid ones and automatically adjust quantities that exceed available stock
        const processedItems = data.map(item => {
          if (!item.product) {
            logger.debug('Cart', `Cart item ${item.id} has null product, will be removed`);
            // Clean up invalid cart items in background
            supabase.from('cart_items').delete().eq('id', item.id).then(({ error }) => {
              if (error) logger.error('Cart', `Failed to clean up invalid cart item ${item.id}`, error);
            });
            return null;
          }

          if (item.product.status === 'archived' || item.product.status === 'sold') {
            logger.debug('Cart', `Cart item ${item.id} product is ${item.product.status}, will be removed`);
            // Clean up outdated cart items in background
            supabase.from('cart_items').delete().eq('id', item.id).then(({ error }) => {
              if (error) logger.error('Cart', `Failed to clean up outdated cart item ${item.id}`, error);
            });
            return null;
          }

          const availableQuantity = Number(item.product.quantity ?? 0);
          if (!Number.isFinite(availableQuantity)) {
            logger.debug('Cart', `Cart item ${item.id} has invalid quantity (${item.product.quantity}), will be removed`);
            supabase.from('cart_items').delete().eq('id', item.id).then(({ error }) => {
              if (error) logger.error('Cart', `Failed to remove invalid cart item ${item.id}`, error);
            });
            return null;
          }
          
          // Only remove if quantity is exactly 0 or less (not just decreased)
          // If quantity > 0, keep the item but it will show the updated available quantity
          if (availableQuantity <= 0) {
            logger.debug('Cart', `Cart item ${item.id} has no remaining stock (quantity: ${availableQuantity}), will be removed`);
            supabase.from('cart_items').delete().eq('id', item.id).then(({ error }) => {
              if (error) logger.error('Cart', `Failed to remove out-of-stock cart item ${item.id}`, error);
            });
            return null;
          }

          // Automatically adjust cart item quantity if it exceeds available stock
          const requestedQuantity = Number(item.quantity ?? 1);
          if (requestedQuantity > availableQuantity) {
            logger.debug('Cart', `Cart item ${item.id} quantity (${requestedQuantity}) exceeds available stock (${availableQuantity}), adjusting...`);
            // Update cart item quantity to match available stock
            supabase.from('cart_items')
              .update({ quantity: availableQuantity })
              .eq('id', item.id)
              .then(({ error }) => {
                if (error) {
                  logger.error('Cart', `Failed to adjust cart item quantity ${item.id}`, error);
                } else {
                  logger.debug('Cart', `Successfully adjusted cart item ${item.id} quantity from ${requestedQuantity} to ${availableQuantity}`);
                  // Invalidate query to ensure UI reflects the update
                  queryClient.invalidateQueries({ queryKey: ['cart', session?.user?.id] }).catch((err) => {
                    logger.warn('Cart', 'Failed to invalidate cart after quantity adjustment', err);
                  });
                }
              });
            // Return the item with adjusted quantity for immediate UI update
            return { ...item, quantity: availableQuantity };
          }

          return item;
        }).filter((item): item is NonNullable<typeof item> => item !== null);
        
        const validItems = processedItems;

        // Process data to extract pendingConfirmation, confirmed, and requestId from meta
        return validItems.map(item => {
          // Handle case where meta field doesn't exist
          try {
            const meta = item.meta || {};
            return {
              ...item,
              pendingConfirmation: meta.pendingConfirmation || false,
              confirmed: meta.confirmed || false,
              requestId: meta.requestId || undefined
            };
          } catch (err) {
            logger.warn('Cart', 'Error processing cart item meta', err);
            // If meta field doesn't exist, return default values
            return {
              ...item,
              pendingConfirmation: false,
              confirmed: false,
              requestId: undefined
            };
          }
        }) as CartItem[];
      } catch (err) {
        logger.error('Cart', 'Unexpected error in cart fetch', err);
        throw err;
      }
    },
    enabled: !!session?.user,
    staleTime: 5000, // Cache for 5 seconds (more responsive)
    retry: (failureCount, error) => {
      // Custom retry logic
      if (failureCount >= 3) return false;
      if (error?.message?.includes('JWT')) return false; // Don't retry auth errors
      if (error?.message?.includes('Network')) return true; // Retry network errors
      return failureCount < 2; // Retry up to 2 times for other errors
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  // Check if there are any pending confirmations in the cart
  const hasPendingConfirmations = (vendorId?: string) => {
    if (vendorId) {
      return cartItems?.some(item => 
        item.product?.vendor_id === vendorId && item.pendingConfirmation
      ) || false;
    }
    return cartItems?.some(item => item.pendingConfirmation) || false;
  };

  // Check if there are any confirmed items in the cart (but not yet purchased)
  const hasConfirmedItems = (vendorId?: string) => {
    if (vendorId) {
      return cartItems?.some(item => 
        item.product?.vendor_id === vendorId && 
        item.confirmed && 
        !item.pendingConfirmation
      ) || false;
    }
    return cartItems?.some(item => item.confirmed && !item.pendingConfirmation) || false;
  };

  // Check if cart is in a locked state (either pending or confirmed)
  const isCartLocked = (vendorId?: string) => {
    return hasPendingConfirmations(vendorId) || hasConfirmedItems(vendorId);
  };

  const addToCart = useMutation({
    mutationFn: async ({ productId, quantity = 1 }: { productId: string; quantity?: number }) => {
      if (!session?.user) throw new Error('Not authenticated');

      // Check for pending confirmations before adding to cart
      if (hasPendingConfirmations()) {
        throw new Error('Cannot add items to cart while waiting for seller confirmation. Please complete or cancel the current request first.');
      }

      // Check if there are confirmed items that would prevent adding new items
      if (hasConfirmedItems()) {
        throw new Error('Cannot add new items to cart after seller confirmation. Complete your purchase or cancel confirmed items first.');
      }

      // Check if item is already in cart for single-unit products
      const { data: existingCartItem, error: cartCheckError } = await supabase
        .from('cart_items')
        .select('id')
        .eq('product_id', productId)
        .eq('user_id', session.user.id)
        .single();

      if (cartCheckError && cartCheckError.code !== 'PGRST116') {
        // PGRST116 is "no rows returned", which is expected if item is not in cart
        throw cartCheckError;
      }

      // Get the product details including vendor information with coordinates and quantity
      const { data: productData, error: productError2 } = await supabase
        .from('products')
        .select(`
          id,
          title,
          quantity,
          vendor_id,
          vendor:vendors (
            id,
            store_name,
            latitude,
            longitude,
            delivery_radius,
            phone_number
          )
        `)
        .eq('id', productId)
        .single();

      if (productError2) throw productError2;
      
      // Check if trying to add multiple units to a single-unit item
      if (Number(productData.quantity ?? 0) <= 0) {
        throw new Error('This item is sold out.');
      }

      if (productData.quantity === 1 && quantity > 1) {
        throw new Error('This item has only 1 unit available.');
      }

      // If item with quantity 1 is already in cart, prevent adding again
      if (existingCartItem && productData.quantity === 1) {
        throw new Error('This item is already in your cart. Single-unit items can only be added once.');
      }
      const product = productData as unknown as ProductWithVendor;

      // Check if product is within delivery range
      if (selectedAddress) {
        const userHasValidCoordinates = GeocodingService.areCoordinatesValid(
          selectedAddress.latitude,
          selectedAddress.longitude
        );
        
        if (userHasValidCoordinates) {
          const vendor = product.vendor;
          const vendorHasValidCoordinates = GeocodingService.areCoordinatesValid(
            vendor?.latitude,
            vendor?.longitude
          );
          
          if (vendorHasValidCoordinates) {
            // Parse delivery radius - default to 10 miles if not set or invalid
            let deliveryRadiusMiles = 10;
            if (vendor.delivery_radius) {
              const parsed = parseFloat(vendor.delivery_radius);
              if (!isNaN(parsed) && parsed > 0) {
                deliveryRadiusMiles = parsed;
              }
            }

            // Calculate distance between user and vendor
            const distance = calculateDistance(
              selectedAddress.latitude!,
              selectedAddress.longitude!,
              vendor.latitude!,
              vendor.longitude!
            );

            if (distance > deliveryRadiusMiles) {
              throw new Error(`This item is not available for delivery to your address. ${vendor.store_name} delivers within ${deliveryRadiusMiles} miles, but you are ${distance.toFixed(1)} miles away.`);
            }
          } else {
            logger.debug('Cart', `Vendor ${vendor?.store_name} missing coordinates - allowing cart addition`);
          }
        } else {
          logger.debug('Cart', 'User delivery address missing coordinates - allowing cart addition');
          // Could trigger geocoding here if needed
        }
      }

      // Get current cart items to check vendor
      const { data: cartData, error: cartError } = await supabase
        .from('cart_items')
        .select(`
          product:products (
            vendor_id,
            vendor:vendors (
              id,
              store_name
            )
          )
        `)
        .eq('user_id', session.user.id);

      if (cartError) throw cartError;
      const currentCartItems = cartData as unknown as CartItemWithVendor[];

      // If there are items in cart, check if they're from the same vendor
      if (currentCartItems && currentCartItems.length > 0) {
        const existingVendorId = currentCartItems[0].product.vendor_id;
        const existingVendor = currentCartItems[0].product.vendor;
        if (product.vendor_id !== existingVendorId) {
          throw new Error(`You can only add items from ${existingVendor.store_name} to your cart. Please remove existing items before adding items from a different seller.`);
        }
      }

      // Check if item already exists in cart and handle accordingly
      if (existingCartItem) {
        // Item exists, update quantity by adding the new quantity
        const currentQuantity = await supabase
          .from('cart_items')
          .select('quantity')
          .eq('id', existingCartItem.id)
          .single();

        if (currentQuantity.error) throw currentQuantity.error;

        const newTotalQuantity = Math.min(
          currentQuantity.data.quantity + quantity,
          productData.quantity
        );

        const { data, error } = await supabase
          .from('cart_items')
          .update({ quantity: newTotalQuantity })
          .eq('id', existingCartItem.id)
          .select()
          .single();

        logger.debug('Cart', 'Update existing cart item response', { data, error });
        if (error) throw error;
        await queryClient.invalidateQueries({ queryKey: ['cart', session.user.id] });
        return data;
      } else {
        // New item, insert it
        const { data, error } = await supabase
          .from('cart_items')
          .insert({
            product_id: productId,
            user_id: session.user.id,
            quantity,
          })
          .select()
          .single();

        logger.debug('Cart', 'Add new cart item response', { data, error });
        if (error) throw error;

        // Track add_to_cart only for brand new cart item inserts
        try {
          trackEvent('add_to_cart', {
            item_id: product.id,
            item_name: product.vendor?.store_name
              ? `${product.vendor.store_name} - ${product.id}`
              : product.id,
            quantity,
            vendor_id: product.vendor_id,
          });
        } catch (analyticsError) {
          logger.warn('Cart', 'Failed to track add_to_cart', analyticsError);
        }

        await queryClient.invalidateQueries({ queryKey: ['cart', session.user.id] });
        return data;
      }
    },
  });

  // Removed verbose cart logging - use React Query DevTools or logger.debug() for debugging

  const updateQuantity = useMutation({
    mutationFn: async ({ id, quantity }: { id: string; quantity: number }) => {
      if (!session?.user) throw new Error('Not authenticated');
      if (!id) throw new Error('Invalid item ID');
      if (quantity < 1) throw new Error('Quantity must be at least 1');

      // Check for pending confirmations before updating quantity
      if (hasPendingConfirmations()) {
        throw new Error('Cannot modify cart quantities while waiting for seller confirmation. Please wait for confirmation or cancel the request.');
      }
      
      // Note: Allow quantity changes for confirmed items (before purchase)

      // Get the cart item and its product to check quantity restrictions
      const { data: cartItem, error: cartError } = await supabase
        .from('cart_items')
        .select(`
          id,
          quantity,
          product:products(quantity, status)
        `)
        .eq('id', id)
        .eq('user_id', session.user.id)
        .single();

      if (cartError && cartError.code !== 'PGRST116') {
        throw new Error(`Failed to find cart item: ${cartError.message}`);
      }

      if (!cartItem) {
        logger.debug('Cart', `Attempted to update non-existent cart item: ${id}`);
        // Item doesn't exist, refresh cart and return
        await queryClient.invalidateQueries({ queryKey: ['cart', session.user.id] });
        return;
      }

      if (!cartItem.product) {
        throw new Error('Product no longer available');
      }

      if (cartItem.product.status === 'sold' || cartItem.product.status === 'archived') {
        throw new Error('Product is no longer available');
      }

      // Block quantity updates for single-unit items
      if (cartItem.product.quantity === 1) {
        throw new Error('Cannot change quantity for single-unit items. Use remove button instead.');
      }

      // Ensure quantity doesn't exceed available stock
      const finalQuantity = Math.min(Math.max(1, quantity), cartItem.product.quantity);

      if (finalQuantity !== quantity) {
        logger.debug('Cart', `Requested quantity ${quantity} capped to available stock ${finalQuantity}`);
      }

      const { error } = await supabase
        .from('cart_items')
        .update({ quantity: finalQuantity })
        .eq('id', id)
        .eq('user_id', session.user.id);

      if (error) {
        logger.error('Cart', `Failed to update quantity for item ${id}`, error);
        throw new Error(`Failed to update quantity: ${error.message}`);
      }

      logger.debug('Cart', `Successfully updated item ${id} quantity to ${finalQuantity}`);
      await queryClient.invalidateQueries({ queryKey: ['cart', session.user.id] });
    },
  });

  const removeItem = useMutation({
    mutationFn: async (id: string) => {
      if (!session?.user) throw new Error('Not authenticated');
      if (!id) throw new Error('Invalid item ID');

      // Check for pending confirmations before removing item
      if (hasPendingConfirmations()) {
        throw new Error('Cannot modify cart while waiting for seller confirmation. Please complete or cancel the current request first.');
      }

      // Verify the item exists and belongs to the user before deletion
      // Also fetch basic product data for analytics
      const { data: existingItem, error: checkError } = await supabase
        .from('cart_items')
        .select(`
          id,
          user_id,
          quantity,
          product:products(
            id,
            title,
            price,
            vendor_id
          )
        `)
        .eq('id', id)
        .eq('user_id', session.user.id)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw new Error(`Failed to verify item: ${checkError.message}`);
      }

      if (!existingItem) {
        logger.debug('Cart', `Attempted to remove non-existent item: ${id}`);
        // Item doesn't exist, but that's not necessarily an error - just refresh cart
        await queryClient.invalidateQueries({ queryKey: ['cart', session.user.id] });
        return;
      }

      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', id)
        .eq('user_id', session.user.id);

      if (error) {
        logger.error('Cart', `Failed to remove item ${id}`, error);
        throw new Error(`Failed to remove item: ${error.message}`);
      }

      logger.debug('Cart', `Successfully removed item ${id}`);

      // Track remove_from_cart with basic item info
      try {
        const product = (existingItem as any).product;
        trackEvent('remove_from_cart', {
          item_id: product?.id || id,
          item_name: product?.title || 'Unknown',
          price: product?.price,
          quantity: existingItem.quantity,
          vendor_id: product?.vendor_id,
        });
      } catch (analyticsError) {
        logger.warn('Cart', 'Failed to track remove_from_cart', analyticsError);
      }
    },
    onMutate: async (id: string) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['cart', session?.user?.id] });

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData(['cart', session?.user?.id]);

      // Optimistically update to remove the item
      queryClient.setQueryData(['cart', session?.user?.id], (old: CartItem[] | undefined) => {
        if (!old) return [];
        return old.filter(item => item.id !== id);
      });

      // Return a context object with the snapshotted value
      return { previousCart };
    },
    onError: (error, _id, context) => {
      logger.error('Cart', 'Remove item error', error);
      
      // Rollback to previous value on error
      if (context?.previousCart) {
        queryClient.setQueryData(['cart', session?.user?.id], context.previousCart);
      }
      
      // Show alert to user about the error
      platformAlert(
        'Remove Failed',
        `Failed to remove item: ${error.message || 'Unknown error'}`,
        [{ text: 'OK' }]
      );
    },
    onSuccess: () => {
      logger.debug('Cart', 'Remove item success');
      // Invalidate and refetch after successful deletion
      queryClient.invalidateQueries({ queryKey: ['cart', session?.user?.id] });
    },
  });

  // New function to cancel confirmed items (allows removal after seller confirmation)
  const cancelConfirmedItem = useMutation({
    mutationFn: async (id: string) => {
      if (!session?.user) throw new Error('Not authenticated');

      // Get the item to check if it's confirmed
      const item = cartItems?.find(i => i.id === id);
      if (!item) throw new Error('Item not found in cart');

      // Only allow cancellation of confirmed items (not pending)
      if (item.pendingConfirmation) {
        throw new Error('Cannot cancel items while confirmation is pending. Wait for seller response first.');
      }

      if (!item.confirmed) {
        throw new Error('This item has not been confirmed by the seller yet.');
      }

      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', id)
        .eq('user_id', session.user.id);

      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ['cart', session.user.id] });
    },
  });

  // Function to clear entire cart
  const clearCart = useMutation({
    mutationFn: async () => {
      if (!session?.user) throw new Error('Not authenticated');

      // Check for pending confirmations before clearing cart
      if (hasPendingConfirmations()) {
        throw new Error('Cannot clear cart while waiting for seller confirmation. Please complete or cancel the current request first.');
      }

      logger.debug('Cart', `Clearing entire cart for user: ${session.user.id}`);

      // Fetch current cart snapshot for analytics before clearing
      const { data: existingItems } = await supabase
        .from('cart_items')
        .select(`
          id,
          quantity,
          product:products(
            id,
            title,
            price,
            vendor_id
          )
        `)
        .eq('user_id', session.user.id);

      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('user_id', session.user.id);

      if (error) {
        logger.error('Cart', 'Failed to clear cart', error);
        throw new Error(`Failed to clear cart: ${error.message}`);
      }

      logger.debug('Cart', 'Successfully cleared cart');

      // Track cart cleared with previous value and item count
      try {
        const items = (existingItems || []) as any[];
        const previousValue = items.reduce(
          (sum, item) =>
            sum + (item.product?.price || 0) * (item.quantity || 0),
          0
        );

        trackEvent('cart_cleared', {
          previous_value: Number(previousValue.toFixed(2)),
          item_count: items.length,
        });
      } catch (analyticsError) {
        logger.warn('Cart', 'Failed to track cart_cleared', analyticsError);
      }

      await queryClient.invalidateQueries({ queryKey: ['cart', session.user.id] });
    },
  });

  return {
    cartItems: cartItems || [],
    isLoading,
    error,
    addToCart,
    updateQuantity: (id: string, change: number) => {
      const item = cartItems?.find(i => i.id === id);
      if (item) {
        updateQuantity.mutate({
          id,
          quantity: item.quantity + change
        });
      }
    },
    removeItem: (id: string) => removeItem.mutate(id),
    cancelConfirmedItem: (id: string) => cancelConfirmedItem.mutate(id),
    clearCart: () => clearCart.mutate(),
    isRemoving: removeItem.isPending,
    isCancelling: cancelConfirmedItem.isPending,
    hasPendingConfirmations,
    hasConfirmedItems,
    isCartLocked,
    updateCartItem: (id: string, updatedItem: Partial<CartItem>) => {
      if (!session?.user) return;

      // Update in-memory cache
      queryClient.setQueryData(['cart', session.user.id], (oldData: CartItem[] | undefined) => {
        if (!oldData) return [];
        return oldData.map(item => 
          item.id === id ? { ...item, ...updatedItem } : item
        );
      });
      
      // Also update in the database if pendingConfirmation, confirmed, or requestId is being set
      if ('pendingConfirmation' in updatedItem || 'confirmed' in updatedItem || 'requestId' in updatedItem)
        (async () => {
          try {
            // First get the current cart item to access its meta
            const { data: currentItems } = await supabase
              .from('cart_items')
              .select('meta')
              .eq('id', id)
              .eq('user_id', session.user.id);

            const currentItem = currentItems?.[0];
            
            // Create updated meta object by merging with existing meta
            const currentMeta = (currentItem?.meta || {}) as Record<string, any>;
            const updatedMeta = { ...currentMeta };
            
            if ('pendingConfirmation' in updatedItem) {
              updatedMeta.pendingConfirmation = updatedItem.pendingConfirmation;
            }
            
            if ('confirmed' in updatedItem) {
              updatedMeta.confirmed = updatedItem.confirmed;
            }
            
            if ('requestId' in updatedItem) {
              updatedMeta.requestId = updatedItem.requestId;
            }
            
            // Update the cart item with the new meta
            await supabase
              .from('cart_items')
              .update({ meta: updatedMeta })
              .eq('id', id)
              .eq('user_id', session.user.id);
          } catch (error) {
            logger.error('Cart', 'Error updating cart item in database', error);
            // Continue without meta update if it fails
          }
        })();
    },
    refreshCart: () => {
      logger.debug('Cart', 'Manually refreshing cart...');
      return refetch();
    },
    debug: {
      sessionUser: session?.user?.id,
      hasItems: (cartItems?.length || 0) > 0,
      itemCount: cartItems?.length || 0
    }
  };
} 